/**
 * Iter-193h — Admin: NDF validation queue.
 *
 * /admin/expenses/validation (CEO, ADMIN, GESTIONNAIRE).
 * Lists every SUBMITTED expense note across all collaborators, lets the user
 * expand a row to see the lines, then validate (→ VALIDATED) or reject (with
 * mandatory reason → REJECTED). VALIDATED notes also surface a "Marquer
 * payée" CTA that triggers the email-with-stamped-PDF flow.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import CrossMonthSearchPanel from "../components/CrossMonthSearchPanel";
import {
  Banknote, Check, ChevronDown, ChevronRight, Copy, Download, FilePlus2, FileText, Loader2, Paperclip, RefreshCw, RotateCcw, ScanLine, X,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const fmt = (n) => Number(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 });
const STATUS_LABEL = { DRAFT: "Brouillon", SUBMITTED: "Soumise", VALIDATED: "Validée", PAID: "Réglée", REJECTED: "Rejetée" };
const STATUS_TONE = {
  DRAFT: "bg-zinc-100 text-zinc-700",
  SUBMITTED: "bg-amber-100 text-amber-900",
  VALIDATED: "bg-blue-100 text-blue-900",
  PAID: "bg-emerald-100 text-emerald-900",
  REJECTED: "bg-red-100 text-red-900",
};

export default function ExpensesValidation() {
  const { user } = useAuth();
  const canActOnPayment = user && (user.role === "CEO" || user.role === "GESTIONNAIRE");
  const canRevoke = user && (user.role === "CEO" || user.role === "GESTIONNAIRE");
  const [tab, setTab] = useState("SUBMITTED"); // SUBMITTED | VALIDATED | PAID | REJECTED
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [openLines, setOpenLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [ndfInvoiceOpen, setNdfInvoiceOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  // Iter-194c — revoke dialog (VALIDATED → DRAFT) requires a motif, included
  // in the cancellation email sent to the collaborator.
  const [revokeFor, setRevokeFor] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");
  // Iter-193s — inline receipt preview state.
  const [previewLine, setPreviewLine] = useState(null);
  // Iter-289 — add attachment / payment reference to a validated line.
  const [settleFor, setSettleFor] = useState(null);
  // Iter-207t — multi-select on the VALIDATED tab : pick a few or all rows
  // and bulk-mark them as paid. Reset whenever the tab or rows change.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkPaying, setBulkPaying] = useState(false);

  // Reset the selection set every time we switch tabs or reload rows.
  useEffect(() => { setSelectedIds(new Set()); }, [tab, rows]);

  const toggleSelected = (noteId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(noteId)) next.delete(noteId); else next.add(noteId);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map((r) => r.expense_note_id));
    });
  };
  const selectedTotal = rows
    .filter((r) => selectedIds.has(r.expense_note_id))
    .reduce((acc, r) => acc + Number(r.total_mad || 0), 0);
  // Iter-283 — copy the selected NDF references (comma-separated) to clipboard.
  const copySelectedRefs = async () => {
    const refs = rows
      .filter((r) => selectedIds.has(r.expense_note_id))
      .map((r) => r.reference)
      .filter(Boolean);
    if (refs.length === 0) { toast.warning("Aucune référence disponible sur les NDF sélectionnées."); return; }
    const text = refs.join(", ");
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success(`${refs.length} référence(s) copiée(s) : ${text.length > 80 ? `${text.slice(0, 80)}…` : text}`);
  };
  const bulkMarkPaid = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Marquer ${selectedIds.size} NDF comme réglées (total ${fmt(selectedTotal)} MAD) ? Les collaborateurs recevront un email.`)) return;
    setBulkPaying(true);
    try {
      const r = await api.post("/expenses/notes/mark-paid-batch", {
        expense_note_ids: Array.from(selectedIds),
      });
      const paid = r.data?.paid_count ?? 0;
      const total = r.data?.total_paid_mad ?? 0;
      toast.success(`${paid} NDF réglée(s) — total ${fmt(total)} MAD`);
      const skipped = (r.data?.results || []).filter((x) => !x.ok);
      if (skipped.length > 0) {
        toast.warning(`${skipped.length} NDF non traitées (statut incorrect ou introuvables).`);
      }
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk paiement échoué");
    } finally {
      setBulkPaying(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      // We hit the queue endpoint for SUBMITTED, otherwise we ask the admin-users
      // listing collapsed by status (single GET per tab).
      let url;
      if (tab === "SUBMITTED") {
        url = "/expenses/admin/queue";
      } else {
        url = `/expenses/admin/queue/by-status/${tab}`;
      }
      const r = await api.get(url);
      setRows(r.data.notes || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const reloadOpenLines = async () => {
    if (!openId) return;
    const note = rows.find((r) => r.expense_note_id === openId);
    if (!note) return;
    try {
      const r = await api.get(`/expenses/admin/users/${note.user_id}/notes/${openId}`);
      setOpenLines(r.data.lines || []);
    } catch { /* keep current lines */ }
  };

  const toggle = async (note) => {
    if (openId === note.expense_note_id) {
      setOpenId(null);
      setOpenLines([]);
      return;
    }
    setOpenId(note.expense_note_id);
    try {
      const r = await api.get(`/expenses/admin/users/${note.user_id}/notes/${note.expense_note_id}`);
      setOpenLines(r.data.lines || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    }
  };

  const validate = async (noteId) => {
    if (!confirm("Valider cette note de frais ? Le collaborateur recevra une notification.")) return;
    try {
      await api.patch(`/expenses/notes/${noteId}/validate`);
      toast.success("NDF validée. Le collaborateur a été notifié.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };
  const reject = async () => {
    if (!rejectReason.trim() || rejectReason.length < 3) {
      toast.error("Motif obligatoire (3 caractères minimum)");
      return;
    }
    try {
      await api.patch(`/expenses/notes/${rejectFor.expense_note_id}/reject`, { reason: rejectReason.trim() });
      toast.success("NDF rejetée. Le collaborateur a été notifié.");
      setRejectFor(null); setRejectReason("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };
  // Iter-235 — settlement dialog (payment details + optional proof file).
  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({});
  const [payFile, setPayFile] = useState(null);
  const [payBusy, setPayBusy] = useState(false);
  const markPaid = (noteId) => {
    const n = (rows || []).find((x) => x.expense_note_id === noteId);
    if (!n) return;
    setPayFor(n);
    setPayFile(null);
    setPayForm({
      paid_date: new Date().toISOString().slice(0, 10),
      payment_mode: "VIREMENT",
      payment_reference: "",
      amount_paid: String(n.total_mad ?? ""),
      notes: "",
    });
  };
  const submitMarkPaid = async () => {
    if (!payFor) return;
    if (payFile && payFile.size > 15 * 1024 * 1024) { toast.error("Justificatif trop volumineux (max 15 Mo)"); return; }
    setPayBusy(true);
    try {
      await api.patch(`/expenses/notes/${payFor.expense_note_id}/mark-paid`, {
        paid_date: payForm.paid_date,
        payment_mode: payForm.payment_mode,
        payment_reference: (payForm.payment_reference || "").trim() || null,
        amount_paid: parseFloat(payForm.amount_paid) || null,
        notes: (payForm.notes || "").trim() || null,
      });
      if (payFile) {
        try {
          const fd = new FormData();
          fd.append("file", payFile);
          await api.post(`/expenses/notes/${payFor.expense_note_id}/payment-proof`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success("NDF payée — justificatif archivé dans Drive (dossier Réglée). Email envoyé au collaborateur.");
        } catch (pe) {
          toast.warning(`NDF payée, mais l'upload du justificatif a échoué : ${pe?.response?.data?.detail || "erreur"}`);
        }
      } else {
        toast.success("NDF payée. Email envoyé au collaborateur.");
      }
      setPayFor(null);
      setPayFile(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
    finally { setPayBusy(false); }
  };
  // Iter-193s/194c — VALIDATED → DRAFT (only if not yet PAID). A motif is
  // required and propagated to the cancellation email + stamped on the PDF.
  const revoke = async () => {
    if (!revokeFor) return;
    if (!revokeReason.trim() || revokeReason.length < 3) {
      toast.error("Le motif doit comporter au moins 3 caractères.");
      return;
    }
    try {
      await api.patch(`/expenses/notes/${revokeFor.expense_note_id}/revoke-validation`, { motif: revokeReason.trim() });
      toast.success("NDF rouverte. Le collaborateur a été notifié avec le motif.");
      setRevokeFor(null);
      setRevokeReason("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };
  const download = async (noteId, kind, ymLabel) => {
    try {
      const r = await api.get(`/expenses/notes/${noteId}/export.${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `NDF-${ymLabel}.${kind}`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec export"); }
  };

  const tabs = ["SUBMITTED", "VALIDATED", "PAID", "REJECTED"];

  return (
    <div className="px-4 sm:px-6 py-6 max-w-6xl mx-auto" data-testid="expenses-validation-page">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight inline-flex items-center gap-2">
            <ScanLine size={20} className="text-zinc-700" />
            Validation notes de frais
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            File d'attente des NDF à valider. Validation → email & notification collaborateur. Mark Paid → PDF tamponné en pièce jointe.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setNdfInvoiceOpen(true)}
            data-testid="ndf-invoice-cta"
            className="inline-flex items-center gap-1.5 px-3 h-9 border border-emerald-200 bg-emerald-50 text-emerald-900 text-xs rounded-md hover:bg-emerald-100"
            title="Émettre une facture client agrégeant les NDF refacturables payées d'une mission"
          >
            <Banknote size={12} /> Facture refacturable
          </button>
          <button onClick={load} disabled={loading} data-testid="expenses-validation-refresh"
            className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-200 text-zinc-700 text-xs rounded-md hover:bg-zinc-50 disabled:opacity-50">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh
          </button>
        </div>
      </div>

      {/* Iter-228x-bis — Cross-month search over APPROVED/PAID NDF lines. */}
      <div className="mb-4">
        <CrossMonthSearchPanel
          kind="validated"
          testidPrefix="ndf-val-search"
          csvFilenamePrefix="ndf_validees"
          categories={["Restauration","Carburant","Hôtel","Transport","Alimentation","Électronique","Loisirs","Abonnements","Loyer","Licences logicielles","Honoraires","Fournitures","Marketing","Banque","Comptabilité","Juridique","Salaires","Charges sociales","Télécom & Internet","Formation","Assurances","Autre"]}
        />
      </div>

      {/* Status tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-zinc-200" data-testid="expenses-validation-tabs">
        {tabs.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setTab(s)}
            data-testid={`expenses-validation-tab-${s}`}
            className={`px-3 py-2 text-xs font-mono uppercase tracking-wider border-b-2 transition-colors ${
              tab === s ? "border-zinc-950 text-zinc-950 font-bold" : "border-transparent text-zinc-500 hover:text-zinc-700"
            }`}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        {/* Iter-207t — Header row for the VALIDATED tab : select-all checkbox so the
            CEO/GESTIONNAIRE can pick all rows at once and bulk mark them paid. */}
        {tab === "VALIDATED" && canActOnPayment && rows.length > 0 && (
          <div className="px-4 py-2 border-b border-zinc-100 bg-zinc-50/60 flex items-center gap-3 text-[11px]" data-testid="expenses-validation-bulk-header">
            <input
              type="checkbox"
              checked={selectedIds.size === rows.length && rows.length > 0}
              onChange={toggleSelectAll}
              data-testid="expenses-validation-select-all"
              className="h-4 w-4 cursor-pointer accent-emerald-600"
              title="Tout sélectionner / Tout désélectionner"
            />
            <span className="font-mono uppercase tracking-wider text-zinc-500">
              {selectedIds.size > 0
                ? `${selectedIds.size} NDF sélectionnée(s)`
                : "Cocher pour sélectionner les NDF à régler"}
            </span>
          </div>
        )}
        {rows.length === 0 && !loading && (
          <div className="px-4 py-14 text-center text-sm text-zinc-500" data-testid="expenses-validation-empty">
            Aucune NDF dans cette catégorie.
          </div>
        )}
        {rows.map((n) => {
          const isOpen = openId === n.expense_note_id;
          const ym = `${n.year}-${String(n.month).padStart(2, "0")}`;
          const selectable = tab === "VALIDATED" && canActOnPayment;
          const isSelected = selectedIds.has(n.expense_note_id);
          return (
            <div key={n.expense_note_id} className="border-b border-zinc-100 last:border-b-0" data-testid={`expenses-validation-row-${n.expense_note_id}`}>
              {/* Iter-207y — On mobile, the row content overflows the
                  viewport (collab name + period + ref badge + status + n_lines
                  + amount don't fit on a <420px screen). Wrap the button in
                  a horizontally scrollable container so users can swipe to
                  see the right-aligned amount + reference. The checkbox
                  stays anchored on the left (outside the scroll zone). */}
              <div className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-50/60">
                {selectable && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => { e.stopPropagation(); toggleSelected(n.expense_note_id); }}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`expenses-validation-row-checkbox-${n.expense_note_id}`}
                    title="Sélectionner pour règlement groupé"
                    className="h-4 w-4 cursor-pointer accent-emerald-600 shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0 overflow-x-auto -mx-2 px-2">
                  <button
                    type="button"
                    onClick={() => toggle(n)}
                    data-testid={`expvalid-note-toggle-${n.expense_note_id}`}
                    className="flex items-center gap-3 text-left min-w-max w-full"
                  >
                    {isOpen ? <ChevronDown size={14} className="shrink-0" /> : <ChevronRight size={14} className="shrink-0" />}
                    <span className="font-mono text-xs font-medium w-44 truncate shrink-0">{n.user_name}</span>
                    <span className="font-mono text-xs w-24 shrink-0">{String(n.month).padStart(2, "0")}/{n.year}</span>
                    {/* Iter-207t — règlement reference badge (visible in tableau alongside status). */}
                    {n.reference && (
                      <span
                        data-testid={`expenses-validation-reference-${n.expense_note_id}`}
                        title="Référence de règlement"
                        className="px-2 py-0.5 rounded-md text-[10px] font-mono font-bold uppercase tracking-wider bg-[#1F4FA3]/10 text-[#1F4FA3] border border-[#1F4FA3]/25 shrink-0 whitespace-nowrap"
                      >
                        {n.reference}
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-mono uppercase shrink-0 whitespace-nowrap ${STATUS_TONE[n.status] || STATUS_TONE.DRAFT}`}>
                      {STATUS_LABEL[n.status] || n.status}
                    </span>
                    {/* Iter-236 — payment proof badge (PAID tab): open the Drive proof. */}
                    {n.status === "PAID" && n.payment_proof?.drive_web_view_link && (
                      <a
                        href={n.payment_proof.drive_web_view_link}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`exp-proof-badge-${n.expense_note_id}`}
                        title={`Justificatif de règlement : ${n.payment_proof.filename || ""}`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 shrink-0 whitespace-nowrap"
                      >
                        <Paperclip size={10} /> Justificatif
                      </a>
                    )}
                    <span className="text-xs text-zinc-500 font-mono ml-2 shrink-0 whitespace-nowrap">{n.n_lines} ligne(s)</span>
                    <span className="text-xs text-zinc-900 font-mono ml-4 font-semibold shrink-0 whitespace-nowrap pr-2">{fmt(n.total_mad)} MAD</span>
                  </button>
                </div>
              </div>
              {isOpen && (
                <div className="px-4 py-3 bg-zinc-50 border-t border-zinc-100">
                  {/* Iter-195y — Surface lines where the collaborator overrode
                      the duplicate flag. The validator must double-check them
                      during the final review. */}
                  {(() => {
                    const overridden = openLines.filter((l) => l.overridden_duplicate);
                    if (overridden.length === 0) return null;
                    return (
                      <div
                        data-testid={`expenses-validation-override-banner-${n.expense_note_id}`}
                        className="mb-3 flex items-start gap-2 text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm"
                      >
                        <ChevronRight size={14} className="mt-0.5 shrink-0 text-amber-700" />
                        <div className="flex-1">
                          <div className="font-semibold">
                            {overridden.length} ligne(s) marquée(s) « pas un doublon » par le collaborateur
                          </div>
                          <div className="text-[12px] text-amber-800 mt-1">
                            Le système avait détecté ces lignes comme potentiels doublons. Le collaborateur a confirmé qu'elles sont distinctes — vérifiez les justificatifs avant de valider.
                          </div>
                          {/* Iter-195z — show each line + its reason (if any) */}
                          <ul className="mt-2 space-y-1 text-[12px]" data-testid={`expenses-validation-override-list-${n.expense_note_id}`}>
                            {overridden.map((l) => (
                              <li
                                key={l.expense_line_id}
                                className="flex items-start gap-2 bg-white/60 border border-amber-200 rounded px-2 py-1"
                                data-testid={`expenses-validation-override-item-${l.expense_line_id}`}
                              >
                                <span className="font-mono shrink-0 text-amber-900">{l.invoice_date}</span>
                                <span className="font-medium">{l.vendor}</span>
                                <span className="font-mono text-amber-800">· {Number(l.amount || 0).toFixed(2)} {l.currency}</span>
                                {l.invoice_number && <span className="font-mono text-amber-700">· n° {l.invoice_number}</span>}
                                {l.not_duplicate_override_reason ? (
                                  <span className="italic text-amber-900 break-words">— « {l.not_duplicate_override_reason} »</span>
                                ) : (
                                  <span className="italic text-amber-600">— (pas de motif)</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    );
                  })()}
                  <div className="flex justify-end gap-2 mb-3 flex-wrap">
                    <button onClick={() => download(n.expense_note_id, "pdf", `${n.user_name?.replace(/\s+/g, "_")}-${ym}`)}
                      data-testid={`exp-val-pdf-${n.expense_note_id}`}
                      className="inline-flex items-center gap-1.5 px-3 h-8 border border-zinc-200 bg-white text-zinc-700 text-xs rounded-md hover:bg-zinc-50">
                      <Download size={12} /> PDF
                    </button>
                    <button onClick={() => download(n.expense_note_id, "csv", `${n.user_name?.replace(/\s+/g, "_")}-${ym}`)}
                      data-testid={`exp-val-csv-${n.expense_note_id}`}
                      className="inline-flex items-center gap-1.5 px-3 h-8 border border-zinc-200 bg-white text-zinc-700 text-xs rounded-md hover:bg-zinc-50">
                      <Download size={12} /> CSV
                    </button>
                    {n.status === "SUBMITTED" && (
                      <>
                        <button onClick={() => { setRejectFor(n); setRejectReason(""); }}
                          data-testid={`exp-val-reject-${n.expense_note_id}`}
                          className="inline-flex items-center gap-1.5 px-3 h-8 border border-red-200 bg-white text-red-700 text-xs rounded-md hover:bg-red-50">
                          <X size={12} /> Rejeter
                        </button>
                        <button onClick={() => validate(n.expense_note_id)}
                          data-testid={`exp-val-validate-${n.expense_note_id}`}
                          className="inline-flex items-center gap-1.5 px-3 h-8 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700">
                          <Check size={12} /> Valider
                        </button>
                      </>
                    )}
                    {canActOnPayment && n.status === "VALIDATED" && (
                      <>
                        <button onClick={() => { setRevokeFor(n); setRevokeReason(""); }}
                          data-testid={`exp-val-revoke-${n.expense_note_id}`}
                          className="inline-flex items-center gap-1.5 px-3 h-8 border border-amber-200 bg-white text-amber-800 text-xs rounded-md hover:bg-amber-50"
                          title="Réouvrir (VALIDATED → DRAFT). Possible uniquement avant paiement. Un motif sera demandé.">
                          <RotateCcw size={12} /> Réouvrir
                        </button>
                        <button onClick={() => markPaid(n.expense_note_id)}
                          data-testid={`exp-val-paid-${n.expense_note_id}`}
                          className="inline-flex items-center gap-1.5 px-3 h-8 bg-emerald-600 text-white text-xs rounded-md hover:bg-emerald-700">
                          <Banknote size={12} /> Marquer payée
                        </button>
                      </>
                    )}
                    <Link to={`/admin/users/${n.user_id}/expenses`}
                      className="inline-flex items-center gap-1.5 px-3 h-8 border border-zinc-200 bg-white text-zinc-700 text-xs rounded-md hover:bg-zinc-50">
                      Voir tout l'historique
                    </Link>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm bg-white rounded border border-zinc-200">
                      <thead className="bg-zinc-100">
                        <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
                          <th className="px-3 py-2">Date</th>
                          <th className="px-3 py-2">Fournisseur</th>
                          <th className="px-3 py-2">Catégorie</th>
                          <th className="px-3 py-2 text-right">Montant</th>
                          <th className="px-3 py-2">Devise</th>
                          <th className="px-3 py-2 text-right">Conv. MAD</th>
                          <th className="px-3 py-2">Facturable</th>
                          <th className="px-3 py-2">Client</th>
                          <th className="px-3 py-2 text-center">Justif.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {openLines.length === 0 && (
                          <tr><td colSpan={9} className="px-3 py-6 text-center text-zinc-500 text-xs">Aucune ligne.</td></tr>
                        )}
                        {openLines.map((l) => (
                          <tr
                            key={l.expense_line_id}
                            className={`border-t border-zinc-100 ${l.overridden_duplicate ? "bg-amber-50/40" : ""}`}
                            title={l.overridden_duplicate ? "Cette ligne a été marquée « pas un doublon » par le collaborateur — à vérifier." : undefined}
                            data-testid={`expvalid-row-${l.expense_line_id}`}
                          >
                            <td className="px-3 py-2 font-mono text-xs">
                              {l.overridden_duplicate && (
                                <span
                                  className="inline-flex items-center px-1 py-0.5 mr-1 rounded text-[9px] font-mono uppercase bg-amber-200 text-amber-900"
                                  data-testid={`expvalid-row-override-badge-${l.expense_line_id}`}
                                  title="Doublon détecté par le système et confirmé « distinct » par le collaborateur"
                                >
                                  Override
                                </span>
                              )}
                              {l.invoice_date}
                            </td>
                            <td className="px-3 py-2 text-sm">
                              {l.vendor}
                              {l.payment_reference && (
                                <div className="text-[10px] font-mono text-sky-700 mt-0.5" data-testid={`expvalid-payref-${l.expense_line_id}`}>
                                  Réf. virement : {l.payment_reference}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-xs">{l.category}</td>
                            <td className="px-3 py-2 text-right font-mono text-xs">
                              {fmt(l.amount)}
                              {(l.currency || "").toUpperCase() === "MAD" && (l.vat_amount != null || l.amount_ht != null) && (
                                <div
                                  className="text-[9px] text-zinc-500 mt-0.5"
                                  data-testid={`expvalid-row-vat-${l.expense_line_id}`}
                                >
                                  HT {l.amount_ht != null ? fmt(l.amount_ht) : "—"} · TVA {l.vat_amount != null ? fmt(l.vat_amount) : "—"}
                                  {l.vat_rate != null && <span className="ml-0.5">({Number(l.vat_rate).toFixed(0)}%)</span>}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">{l.currency}</td>
                            <td className="px-3 py-2 text-right font-mono text-xs font-semibold">{fmt(l.amount_mad)}</td>
                            <td className="px-3 py-2 text-xs" data-testid={`expvalid-billable-${l.expense_line_id}`}>
                              {l.billable_to_client ? (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-900 font-mono text-[10px]">Oui</span>
                              ) : (
                                <span className="text-zinc-400 font-mono text-[10px]">Non</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-xs text-zinc-700" data-testid={`expvalid-client-${l.expense_line_id}`}>
                              {l.billable_to_client ? (l.client_name || l.mission_title || "—") : "—"}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <div className="inline-flex items-center gap-1">
                                {(l.receipt_drive_file_id || l.staged_file_id || l.receipt_storage_path) ? (
                                  <button
                                    type="button"
                                    onClick={() => setPreviewLine(l)}
                                    data-testid={`expvalid-preview-${l.expense_line_id}`}
                                    title="Voir le justificatif"
                                    className="inline-flex items-center justify-center w-7 h-7 rounded border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                                  >
                                    <Paperclip size={12} />
                                  </button>
                                ) : (
                                  <span className="text-zinc-300 font-mono text-[10px]">—</span>
                                )}
                                {(tab === "VALIDATED" || tab === "PAID") && (
                                  <button
                                    type="button"
                                    onClick={() => setSettleFor(l)}
                                    data-testid={`expvalid-settle-edit-${l.expense_line_id}`}
                                    title="Ajouter un justificatif ou une référence de virement (seuls ces champs sont modifiables)"
                                    className="inline-flex items-center justify-center w-7 h-7 rounded border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100"
                                  >
                                    <FilePlus2 size={12} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {n.rejection_reason && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-xs text-red-900">
                      <strong>Motif du rejet :</strong> {n.rejection_reason}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Iter-207t — Sticky summary bar shown when 1+ VALIDATED NDF is selected.
          Surfaces the running total + a single bulk action button. */}
      {tab === "VALIDATED" && canActOnPayment && selectedIds.size > 0 && (
        <div
          data-testid="expenses-validation-bulk-bar"
          className="sticky bottom-4 mt-5 flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-white border-2 border-emerald-300 rounded-lg shadow-lg"
        >
          <div className="flex items-baseline gap-3 text-sm">
            <span className="font-mono uppercase tracking-wider text-[11px] text-zinc-500">À régler</span>
            <span className="font-mono font-bold text-zinc-900 text-lg" data-testid="expenses-validation-bulk-total">
              {fmt(selectedTotal)} MAD
            </span>
            <span className="text-zinc-500">
              sur <span className="font-semibold text-zinc-900">{selectedIds.size}</span> NDF
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              data-testid="expenses-validation-bulk-clear"
              className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-200 text-zinc-700 text-xs rounded-md hover:bg-zinc-50"
            >
              Annuler la sélection
            </button>
            <button
              type="button"
              onClick={copySelectedRefs}
              title="Copier les références des NDF sélectionnées (séparées par des virgules)"
              data-testid="expenses-validation-bulk-copy-refs"
              className="inline-flex items-center gap-1.5 px-3 h-9 border border-sky-300 bg-sky-50 text-sky-800 text-xs rounded-md hover:bg-sky-100"
            >
              <Copy size={12} />
              Copier les réf ({selectedIds.size})
            </button>
            <button
              type="button"
              onClick={bulkMarkPaid}
              disabled={bulkPaying}
              data-testid="expenses-validation-bulk-mark-paid"
              className="inline-flex items-center gap-1.5 px-4 h-9 bg-emerald-600 text-white text-xs rounded-md hover:bg-emerald-700 disabled:opacity-50"
            >
              {bulkPaying ? <Loader2 size={12} className="animate-spin" /> : <Banknote size={12} />}
              Marquer {selectedIds.size} NDF comme réglée{selectedIds.size > 1 ? "s" : ""}
            </button>
          </div>
        </div>
      )}

      <Dialog open={!!rejectFor} onOpenChange={(o) => { if (!o) { setRejectFor(null); setRejectReason(""); } }}>
        <DialogContent data-testid="expenses-validation-reject-dialog">
          <DialogHeader>
            <DialogTitle>Rejeter la NDF</DialogTitle>
            <DialogDescription>
              {rejectFor && `${rejectFor.user_name} · ${String(rejectFor.month).padStart(2, "0")}/${rejectFor.year}`}
              <br />Précisez le motif. Le collaborateur sera notifié et le motif sera inclus dans l'email + tamponné sur le PDF.
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Justificatif manquant, doublon, dépense non remboursable…"
            rows={4}
            className="w-full px-3 py-2 border border-zinc-300 rounded text-sm"
            data-testid="expenses-validation-reject-reason"
          />
          <DialogFooter>
            <button type="button" onClick={() => { setRejectFor(null); setRejectReason(""); }}
              className="px-3 h-9 text-sm border border-zinc-200 rounded hover:bg-zinc-50">Annuler</button>
            <button type="button" onClick={reject} data-testid="expenses-validation-reject-confirm"
              className="px-3 h-9 text-sm bg-red-600 text-white rounded hover:bg-red-700">Rejeter la NDF</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-235 — Settlement dialog: payment details + optional proof file. */}
      <Dialog open={!!payFor} onOpenChange={(o) => { if (!o) { setPayFor(null); setPayFile(null); } }}>
        <DialogContent data-testid="expenses-mark-paid-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Régler la NDF {payFor?.reference || ""}</DialogTitle>
            <DialogDescription>
              {payFor && `${payFor.user_name} · ${String(payFor.month).padStart(2, "0")}/${payFor.year} · ${fmt(payFor.total_mad)} MAD`}
              <br />Un email avec le PDF tamponné sera envoyé au collaborateur.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Date de règlement</label>
                <input type="date" value={payForm.paid_date || ""} onChange={(e) => setPayForm({ ...payForm, paid_date: e.target.value })}
                  data-testid="exp-paid-date" className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-md" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Montant réglé (MAD)</label>
                <input type="number" step="0.01" value={payForm.amount_paid || ""} onChange={(e) => setPayForm({ ...payForm, amount_paid: e.target.value })}
                  data-testid="exp-paid-amount" className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-md" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Mode</label>
                <select value={payForm.payment_mode || "VIREMENT"} onChange={(e) => setPayForm({ ...payForm, payment_mode: e.target.value })}
                  data-testid="exp-paid-mode" className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-md bg-white">
                  <option value="VIREMENT">Virement</option>
                  <option value="CHEQUE">Chèque</option>
                  <option value="ESPECES">Espèces</option>
                  <option value="CARTE">Carte</option>
                  <option value="AUTRE">Autre</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Référence du règlement</label>
                <input value={payForm.payment_reference || ""} onChange={(e) => setPayForm({ ...payForm, payment_reference: e.target.value })}
                  placeholder="N° virement…" data-testid="exp-paid-reference" className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-md" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Justificatif du règlement — PDF ou image (facultatif)</label>
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp"
                onChange={(e) => setPayFile(e.target.files?.[0] || null)}
                data-testid="exp-paid-proof-input"
                className="w-full text-xs file:mr-3 file:px-3 file:h-8 file:rounded-md file:border-0 file:bg-zinc-100 file:text-zinc-700 file:text-xs hover:file:bg-zinc-200 file:cursor-pointer" />
              <p className="text-[10px] text-zinc-500 mt-1">Archivé sur Drive : {"{parent}/{Collaborateur}/Réglée/"}</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Remarques (optionnel)</label>
              <textarea value={payForm.notes || ""} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                rows={2} data-testid="exp-paid-notes" className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-md resize-y" />
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => { setPayFor(null); setPayFile(null); }}
              className="px-3 h-9 text-sm border border-zinc-200 rounded hover:bg-zinc-50">Annuler</button>
            <button type="button" onClick={submitMarkPaid} disabled={payBusy} data-testid="exp-paid-confirm"
              className="inline-flex items-center gap-2 px-3 h-9 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
              <Banknote size={14} /> {payBusy ? "Règlement…" : "Valider le règlement"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-194c — Revoke dialog (VALIDATED → DRAFT) — motif obligatoire,
          inclus dans le mail "Annulée" + tampon PDF. */}
      <Dialog open={!!revokeFor} onOpenChange={(o) => { if (!o) { setRevokeFor(null); setRevokeReason(""); } }}>
        <DialogContent data-testid="expenses-validation-revoke-dialog">
          <DialogHeader>
            <DialogTitle>Réouvrir la NDF validée</DialogTitle>
            <DialogDescription>
              {revokeFor && `${revokeFor.user_name} · ${String(revokeFor.month).padStart(2, "0")}/${revokeFor.year}`}
              <br />La NDF repassera en BROUILLON. Indiquez le motif — il sera inclus dans l'email "Annulée" envoyé au collaborateur et tamponné sur le PDF.
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            placeholder="Erreur de validation, justificatif à ré-attacher, montant à corriger…"
            rows={4}
            className="w-full px-3 py-2 border border-zinc-300 rounded text-sm"
            data-testid="expenses-validation-revoke-reason"
          />
          <DialogFooter>
            <button type="button" onClick={() => { setRevokeFor(null); setRevokeReason(""); }}
              className="px-3 h-9 text-sm border border-zinc-200 rounded hover:bg-zinc-50">Annuler</button>
            <button type="button" onClick={revoke} data-testid="expenses-validation-revoke-confirm"
              className="px-3 h-9 text-sm bg-amber-600 text-white rounded hover:bg-amber-700">Réouvrir la NDF</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NdfInvoiceDialog open={ndfInvoiceOpen} onClose={() => setNdfInvoiceOpen(false)} onIssued={load} />

      {/* Iter-193s — inline receipt preview (image or PDF) */}
      <ReceiptPreviewDialog line={previewLine} onClose={() => setPreviewLine(null)} />
      {/* Iter-289 — add attachment / payment reference on a validated line */}
      <SettlementInfoDialog line={settleFor} onClose={() => setSettleFor(null)} onSaved={reloadOpenLines} />
    </div>
  );
}


// Iter-289 — Dialog to complete a VALIDATED/PAID line: attachment and/or bank
// transfer reference ONLY (no other field is editable once validated).
function SettlementInfoDialog({ line, onClose, onSaved }) {
  const [ref, setRef] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);
  useEffect(() => {
    setRef(line?.payment_reference || "");
    setFile(null);
    setHistory(null);
    if (line?.expense_line_id) {
      api.get(`/admin/expenses/lines/${line.expense_line_id}/settlement-history`)
        .then((r) => setHistory(r.data?.items || []))
        .catch(() => setHistory([]));
    }
  }, [line]);
  const save = async () => {
    if (!file && (ref || "") === (line?.payment_reference || "")) {
      toast.warning("Rien à enregistrer : joignez un fichier ou modifiez la référence.");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      if ((ref || "").trim() && ref !== (line?.payment_reference || "")) fd.append("payment_reference", ref);
      if (file) fd.append("file", file);
      const r = await api.post(`/admin/expenses/lines/${line.expense_line_id}/settlement-info`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (r.data?.receipt_target === "drive") {
        toast.success("Justificatif ajouté au Google Drive du collaborateur.");
      } else if (r.data?.receipt_target === "storage") {
        toast.warning("Drive indisponible — justificatif conservé dans le stockage sécurisé de l'app.");
      } else {
        toast.success("Référence de virement enregistrée.");
      }
      onClose();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement échoué");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!line} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="settlement-info-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FilePlus2 size={16} /> Compléter la ligne — {line?.vendor}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {line?.invoice_date} · {fmt(line?.amount_mad)} MAD — NDF validée : seuls le justificatif et la
            référence de virement peuvent être ajoutés, aucune autre information n&apos;est modifiable.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-zinc-700">Référence de virement</label>
            <input
              type="text"
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              maxLength={200}
              placeholder="Ex. VIR-2026-09-1234"
              data-testid="settlement-info-ref-input"
              className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-200"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-zinc-700">Pièce jointe (justificatif)</label>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="settlement-info-file-input"
              className="w-full text-xs"
            />
            {(line?.receipt_drive_file_id || line?.receipt_storage_path) && (
              <p className="text-[10px] text-zinc-500">
                Un justificatif existe déjà — le nouveau fichier le remplacera à l&apos;affichage.
              </p>
            )}
          </div>
          {/* Iter-290 — audit trail */}
          <div className="space-y-1" data-testid="settlement-history">
            <label className="text-xs font-medium text-zinc-700">Historique des modifications</label>
            {history === null ? (
              <p className="text-[11px] text-zinc-400">Chargement…</p>
            ) : history.length === 0 ? (
              <p className="text-[11px] text-zinc-400" data-testid="settlement-history-empty">Aucune modification enregistrée.</p>
            ) : (
              <ul className="border border-zinc-100 rounded-md divide-y divide-zinc-100 max-h-36 overflow-y-auto">
                {history.map((h) => (
                  <li key={h.audit_id} className="px-2.5 py-1.5 text-[11px] flex items-start gap-2" data-testid={`settlement-history-item-${h.audit_id}`}>
                    <span className="font-mono text-zinc-400 shrink-0">{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                    <span className="text-zinc-700">
                      <b>{h.actor_name}</b>{" "}
                      {h.action === "RECEIPT_ADDED"
                        ? <>a ajouté le justificatif « {h.detail} » {h.target === "drive" ? "(Google Drive)" : "(stockage app)"}</>
                        : <>a défini la référence de virement « {h.detail} »</>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="px-3 h-9 text-xs border border-zinc-200 rounded-md hover:bg-zinc-50">Annuler</button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="settlement-info-save"
            className="inline-flex items-center gap-1.5 px-3 h-9 text-xs rounded-md bg-sky-700 text-white hover:bg-sky-800 disabled:opacity-50"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            Enregistrer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Iter-193s — Inline preview dialog for receipts attached to an expense line.
// Proxies the file content through /api/expenses/receipts/{line_id}/preview so
// the browser can render it inside the app (no extra Drive tab).
function ReceiptPreviewDialog({ line, onClose }) {
  if (!line) return null;
  const url = `/api/expenses/receipts/${line.expense_line_id}/preview`;
  return (
    <Dialog open={!!line} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="receipt-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText size={16} /> Justificatif — {line.vendor}
          </DialogTitle>
          <DialogDescription>
            {line.invoice_date} · {fmt(line.amount)} {line.currency} ({fmt(line.amount_mad)} MAD)
          </DialogDescription>
        </DialogHeader>
        {/* Iter-193x — single <iframe> handles both images AND PDFs natively
            (browser sniffs content-type from the response). This fixes the
            previous bug where PDF receipts only fell back to <object> on img
            error, often leaving a blank dialog. */}
        <div className="bg-zinc-50 border border-zinc-200 rounded p-1 overflow-hidden" data-testid="receipt-preview-content">
          <iframe
            src={url}
            title="Justificatif"
            className="w-full h-[68vh] bg-white"
            style={{ border: 0 }}
          />
        </div>
        <DialogFooter>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="px-3 h-9 text-sm border border-zinc-200 rounded hover:bg-zinc-50 inline-flex items-center gap-1"
          >
            Ouvrir dans un nouvel onglet
          </a>
          <button
            type="button"
            onClick={onClose}
            className="px-3 h-9 text-sm border border-zinc-200 rounded hover:bg-zinc-50"
            data-testid="receipt-preview-close"
          >
            Fermer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Iter-193p — Dialog to issue a client invoice from refacturable NDF lines.
function NdfInvoiceDialog({ open, onClose, onIssued }) {
  const [missions, setMissions] = useState([]);
  const [missionId, setMissionId] = useState("");
  const [preview, setPreview] = useState(null);
  const [issuing, setIssuing] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [picked, setPicked] = useState({}); // Iter-320 — lines ticked for the invoice

  useEffect(() => {
    setPicked(Object.fromEntries((preview?.lines || []).map((l) => [l.expense_line_id, true])));
  }, [preview]);
  const pickedLines = (preview?.lines || []).filter((l) => picked[l.expense_line_id]);
  const pickedTotal = pickedLines.reduce((s, l) => s + (Number(l.amount_mad) || 0), 0);

  useEffect(() => {
    if (!open) return;
    api.get("/missions").then((r) => setMissions(r.data || [])).catch(() => setMissions([]));
    setMissionId("");
    setPreview(null);
  }, [open]);

  useEffect(() => {
    if (!missionId) { setPreview(null); return; }
    setLoadingPreview(true);
    api.get(`/expenses/billable-ready/${missionId}`)
      .then((r) => setPreview(r.data))
      .catch(() => setPreview({ lines: [], total_mad: 0 }))
      .finally(() => setLoadingPreview(false));
  }, [missionId]);

  const issue = async () => {
    setIssuing(true);
    try {
      const r = await api.post("/expenses/issue-client-invoice", { mission_id: missionId, line_ids: pickedLines.map((l) => l.expense_line_id) });
      toast.success(`Facture ${r.data.number} créée — ${fmt(r.data.total_ttc)} MAD TTC`);
      onIssued?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'émission");
    } finally {
      setIssuing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="ndf-invoice-dialog">
        <DialogHeader>
          <DialogTitle>Émettre une facture refacturable</DialogTitle>
          <DialogDescription>
            Sélectionnez une mission puis cochez les lignes NDF refacturables à inclure : chaque dépense
            apparaît sur sa propre ligne de la facture client TTC (séquence dédiée <code>NDF-{new Date().getFullYear()}-NNN</code>).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <label className="block">
            <span className="text-xs text-zinc-600 mb-1 block">Mission</span>
            <select value={missionId} onChange={(e) => setMissionId(e.target.value)} data-testid="ndf-invoice-mission-select"
              className="w-full px-2.5 py-1.5 border border-zinc-200 rounded text-sm">
              <option value="">— Sélectionner —</option>
              {missions.map((m) => (
                <option key={m.mission_id} value={m.mission_id}>{m.title}{m.client_name ? ` — ${m.client_name}` : ""}</option>
              ))}
            </select>
          </label>
          {loadingPreview && <div className="text-sm text-zinc-500"><Loader2 size={12} className="inline animate-spin" /> Recherche des lignes…</div>}
          {preview && !loadingPreview && (
            <div className="border border-zinc-200 rounded p-3" data-testid="ndf-invoice-preview">
              {preview.lines.length === 0 ? (
                <div className="text-sm text-zinc-500 text-center py-4">Aucune ligne refacturable disponible.</div>
              ) : (
                <>
                  <div className="text-xs font-mono uppercase text-zinc-500 mb-2" data-testid="ndf-invoice-selection-total">{pickedLines.length}/{preview.lines.length} ligne(s) sélectionnée(s) — Total {fmt(pickedTotal)} MAD</div>
                  <div className="max-h-56 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-zinc-50">
                        <tr>
                          <th className="px-2 py-1 w-6">
                            <input type="checkbox" checked={pickedLines.length === preview.lines.length}
                              onChange={(e) => setPicked(Object.fromEntries(preview.lines.map((l) => [l.expense_line_id, e.target.checked])))}
                              data-testid="ndf-invoice-select-all" />
                          </th>
                          <th className="px-2 py-1 text-left">Date</th><th className="px-2 py-1 text-left">Fournisseur</th><th className="px-2 py-1 text-left">Cat.</th><th className="px-2 py-1 text-right">MAD</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.lines.map((l) => (
                          <tr key={l.expense_line_id} className={`border-t border-zinc-100 ${picked[l.expense_line_id] ? "" : "opacity-50"}`}>
                            <td className="px-2 py-1">
                              <input type="checkbox" checked={!!picked[l.expense_line_id]}
                                onChange={() => setPicked((p) => ({ ...p, [l.expense_line_id]: !p[l.expense_line_id] }))}
                                data-testid={`ndf-invoice-line-check-${l.expense_line_id}`} />
                            </td>
                            <td className="px-2 py-1 font-mono">{l.invoice_date}</td>
                            <td className="px-2 py-1">{l.vendor}</td>
                            <td className="px-2 py-1">{l.category}</td>
                            <td className="px-2 py-1 text-right font-mono">{fmt(l.amount_mad)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-3 h-9 text-sm border border-zinc-200 rounded hover:bg-zinc-50">Annuler</button>
          <button
            onClick={issue}
            disabled={!missionId || !preview || pickedLines.length === 0 || issuing}
            data-testid="ndf-invoice-issue-btn"
            className="px-3 h-9 text-sm bg-emerald-700 text-white rounded hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {issuing ? <Loader2 size={12} className="inline mr-1 animate-spin" /> : null}
            Émettre la facture
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
